/**
 * Your usage (Figma 09 · Your usage 01–08). Not built, no backend yet:
 * Request top-up (top-ups are given by a platform admin, CMS only).
 */
import { budgetCeilingUsd, budgetUsedPct, useAiBudget } from "@/hooks/useAiBudget";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useOrganizationMembers } from "@/app/routes/dashboard/settings/api/useOrganization";
import { ErrorState } from "../../ViewStates";
import { Card, CardLoading, Fact, Facts, Meter, Note, PageHead, SettingsPage } from "../parts/SettingsParts";
import { useYouHead } from "../parts/scope";
import { formatResetDay, formatTime, formatUsd } from "../parts/settingsFormat";

export default function YourUsagePage() {
  const head = useYouHead();
  const { selectedOrganization } = useCurrentUser();
  const isGuest = selectedOrganization?.role === "Guest";
  const { data, isLoading, isError, refetch } = useAiBudget();
  const { data: members } = useOrganizationMembers(isGuest ? null : (selectedOrganization?.organization.id ?? null));
  const owner = members?.find((m) => m.role === "Owner");
  const ownerName = owner?.name || owner?.email;

  const status = ownerName ? `Your AI allowance this month, set by ${ownerName}.` : "Your AI allowance this month.";

  if (isLoading) {
    return (
      <SettingsPage>
        <PageHead {...head} title="Your usage" status="Reading your usage." />
        <CardLoading caption="Loading your usage" />
      </SettingsPage>
    );
  }

  if (isError || !data) {
    return (
      <SettingsPage>
        <PageHead {...head} title="Your usage" status={status} />
        <ErrorState
          title="Your usage did not load"
          body="Creo could not read it. Nothing has changed, and the agents are not affected."
          onRetry={() => void refetch()}
        />
      </SettingsPage>
    );
  }

  const pct = budgetUsedPct(data);
  const ceiling = budgetCeilingUsd(data);
  const left = Math.max(0, ceiling - data.spentUsd);
  const resets = formatResetDay(data.periodEnd);
  const resetMoment = new Date(data.periodEnd);
  const usedUp = data.exhausted || pct >= 100;

  return (
    <SettingsPage>
      <PageHead {...head} title="Your usage" status={status} />
      <Card>
        <Meter
          pct={pct}
          caption="of your allowance used this month"
          left={`${formatUsd(data.spentUsd)} of ${formatUsd(ceiling)}`}
          right={`Resets ${resets}`}
        />
        {usedUp ? (
          <Note tone="alert">
            Your allowance is used up. The agents answer you again on {resets}
            {ownerName ? `, or sooner if ${ownerName} asks Mohtawa for a top-up` : ""}.
          </Note>
        ) : pct >= 80 ? (
          <Note tone="alert">
            You have used {Math.round(pct)}% of this month’s allowance. At 100% the agents stop answering you until{" "}
            {resets}.
          </Note>
        ) : null}
        {data.topUpUsd > 0 ? (
          <Note>
            Your allowance was raised by {formatUsd(data.topUpUsd)} this month, to {formatUsd(ceiling)}.
          </Note>
        ) : null}
        <Facts>
          <Fact label="Left this month">{left > 0 ? formatUsd(left) : "Nothing"}</Fact>
          {ownerName ? <Fact label="Set by">{ownerName}, the owner</Fact> : null}
          <Fact label="Resets">
            {resets}, {Number.isNaN(resetMoment.getTime()) ? "" : `${formatTime(resetMoment)} your time`}
          </Fact>
          {!usedUp ? (
            <Fact label="When it runs out">
              At 100% the agents stop answering you until it resets. Your drafts and posts are not touched.
            </Fact>
          ) : null}
        </Facts>
      </Card>
    </SettingsPage>
  );
}
